import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaGithub, FaLinkedin, FaTwitter, 
  FaCheck, FaCopy, FaNetworkWired, FaBrain, FaMicrochip,
  FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import InteractiveGraph from './InteractiveGraph';
import AnimatedName from './AnimatedName';

const roles = [
  "Distributed Systems Builder",
  "High-Performance Backend Engineer",
  "Machine Learning Practitioner",
  "Algorithmic Problem Solver"
];

const pillars = [
  {
    number: "01",
    title: "Distributed Systems & Microservices",
    description: "Designing fault-tolerant, high-throughput microservices and event-driven architectures with robust RPC and serialization protocols.",
    icon: <FaNetworkWired className="w-6 h-6 text-blue-500 dark:text-blue-400" />,
    badge: "Scale & Reliability",
    tags: ["Go", "C++", "Java", "gRPC", "Protobuf", "Docker", "Kubernetes", "Kafka"]
  },
  {
    number: "02",
    title: "Low-Level Performance & Concurrency",
    description: "Deep dive into memory safety, multithreading, advanced CPU instruction optimizations, and competitive programming algorithms.",
    icon: <FaMicrochip className="w-6 h-6 text-emerald-500 dark:text-emerald-400" />,
    badge: "Speed & Optimization",
    tags: ["Rust", "C++", "Linux", "Concurrency", "System Design", "Networking"]
  },
  {
    number: "03",
    title: "Machine Learning & Intelligent Systems",
    description: "Training neural network models, architecting intelligent data pipelines, and integrating modern AI/ML frameworks into production systems.",
    icon: <FaBrain className="w-6 h-6 text-purple-500 dark:text-purple-400" />,
    badge: "AI & Data",
    tags: ["Python", "TensorFlow", "scikit-learn", "FastAPI", "LangChain"]
  }
];

const telemetryMetrics = [
  { label: "ml::tok_per_sec", type: "number", base: 43, range: 35 },
  { label: "epoll::events_handled", type: "number", base: 3123, range: 250 },
  { label: "grpc::p99_latency", type: "float", base: 0.16, range: 0.04, unit: "ms" },
  { label: "raft::leader_term", type: "fixed", value: "42 (Quorum OK)" },
  { label: "cache::l1_hit_rate", type: "fixed", value: "99.8%" }
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0); // 0 = Hero, 1 = Engineering Architecture
  const [roleIndex, setRoleIndex] = useState(0);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const isScrollingRef = useRef(false);

  // Telemetry thought bubble states
  const [telemetryIndex, setTelemetryIndex] = useState(0);
  const [telemetryValue, setTelemetryValue] = useState('');

  // Rotating roles
  useEffect(() => {
    const timer = setInterval(() => {
      setRoleIndex((prev) => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // Telemetry Thought Bubble Stream & Ubuntu-dock cycling
  useEffect(() => {
    const activeMetric = telemetryMetrics[telemetryIndex];

    // Jitter live value
    const jitterInterval = setInterval(() => {
      if (activeMetric.type === "number") {
        const val = Math.floor(activeMetric.base + (Math.random() - 0.45) * activeMetric.range);
        setTelemetryValue(`${activeMetric.label}: ${val.toLocaleString()}`);
      } else if (activeMetric.type === "float") {
        const val = (activeMetric.base + (Math.random() - 0.45) * activeMetric.range).toFixed(2);
        setTelemetryValue(`${activeMetric.label}: ${val}${activeMetric.unit}`);
      } else {
        setTelemetryValue(`${activeMetric.label}: ${activeMetric.value}`);
      }
    }, 180);

    // Switch to next telemetry metric every 3.6s
    const switchTimer = setTimeout(() => {
      setTelemetryIndex((prev) => (prev + 1) % telemetryMetrics.length);
    }, 3600);

    return () => {
      clearInterval(jitterInterval);
      clearTimeout(switchTimer);
    };
  }, [telemetryIndex]);

  // Single scroll gesture / Arrow key slide transition
  useEffect(() => {
    const triggerSlide = (targetIndex) => {
      if (isScrollingRef.current) return;
      isScrollingRef.current = true;
      setCurrentSlide(targetIndex);
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 750);
    };

    const handleWheel = (e) => {
      if (e.deltaY > 20 && currentSlide === 0) {
        triggerSlide(1);
      } else if (e.deltaY < -20 && currentSlide === 1) {
        triggerSlide(0);
      }
    };

    const handleKeyDown = (e) => {
      if ((e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') && currentSlide === 0) {
        e.preventDefault();
        triggerSlide(1);
      } else if ((e.key === 'ArrowUp' || e.key === 'PageUp') && currentSlide === 1) {
        e.preventDefault();
        triggerSlide(0);
      }
    };

    let touchStartY = 0;
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchEnd = (e) => {
      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchStartY - touchEndY;
      if (deltaY > 35 && currentSlide === 0) {
        triggerSlide(1);
      } else if (deltaY < -35 && currentSlide === 1) {
        triggerSlide(0);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentSlide]);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("roushan@procoder9973.dev");
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  return (
    <div className="relative h-screen w-full overflow-hidden select-text">
      {/* Background Interactive Graph Canvas */}
      <InteractiveGraph />

      {/* Slide Navigation Dots (Right Side) */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-3">
        <button
          onClick={() => setCurrentSlide(0)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${
            currentSlide === 0 
              ? 'bg-primary dark:bg-dark-primary scale-125 shadow-md ring-2 ring-primary/30' 
              : 'bg-zinc-300 dark:bg-zinc-700 hover:bg-zinc-400 dark:hover:bg-zinc-500'
          }`}
          title="Intro Scene"
          aria-label="Navigate to Hero Scene"
        />
        <button
          onClick={() => setCurrentSlide(1)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${
            currentSlide === 1 
              ? 'bg-primary dark:bg-dark-primary scale-125 shadow-md ring-2 ring-primary/30' 
              : 'bg-zinc-300 dark:bg-zinc-700 hover:bg-zinc-400 dark:hover:bg-zinc-500'
          }`}
          title="Architecture Scene"
          aria-label="Navigate to Engineering Architecture Scene"
        />
      </div>

      {/* Main Slideshow Container (Translates Y based on active scene) */}
      <motion.div 
        className="w-full h-full"
        animate={{ y: currentSlide === 0 ? "0%" : "-100%" }}
        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
      >
        
        {/* ========================================================= */}
        {/* SCENE 1: HERO */}
        {/* ========================================================= */}
        <section className="h-screen w-full flex flex-col justify-center items-center relative px-6 z-10">
          <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Column: Headline & Intros */}
            <div className="lg:col-span-7 space-y-5 text-center lg:text-left">
              {/* Status Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>Available for Distributed Systems & ML Engineering</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-zinc-900 dark:text-white leading-[1.1]">
                Hi, I'm <AnimatedName text="Roushan" />
              </h1>

              {/* Dynamic Role Cycler */}
              <div className="h-10 sm:h-12 flex items-center justify-center lg:justify-start overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={roles[roleIndex]}
                    className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gradient-primary"
                    initial={{ y: 25, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -25, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                  >
                    {roles[roleIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* Bio Paragraph */}
              <p className="max-w-xl mx-auto lg:mx-0 text-base sm:text-lg text-zinc-600 dark:text-zinc-300 leading-relaxed font-normal">
                I specialize in building robust, scalable backend architectures and high-throughput distributed systems. Now diving deep into the fascinating world of machine learning to push computational boundaries even further.
              </p>

              {/* Social Links */}
              <div className="flex items-center justify-center lg:justify-start gap-3 pt-2 text-zinc-500 dark:text-zinc-400">
                <a 
                  href="https://github.com/github-roushan" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-zinc-100 dark:bg-zinc-800/60 hover:text-zinc-900 dark:hover:text-white border border-zinc-200/60 dark:border-zinc-700/60 transition-colors"
                  aria-label="GitHub"
                >
                  <FaGithub size={18} />
                </a>
                <a 
                  href="https://www.linkedin.com/in/pro-coder-roshu/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-zinc-100 dark:bg-zinc-800/60 hover:text-zinc-900 dark:hover:text-white border border-zinc-200/60 dark:border-zinc-700/60 transition-colors"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin size={18} />
                </a>
                <a 
                  href="https://x.com/procoder973" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-zinc-100 dark:bg-zinc-800/60 hover:text-zinc-900 dark:hover:text-white border border-zinc-200/60 dark:border-zinc-700/60 transition-colors"
                  aria-label="Twitter / X"
                >
                  <FaTwitter size={18} />
                </a>
                <button
                  onClick={handleCopyEmail}
                  className="p-2.5 rounded-lg bg-zinc-100 dark:bg-zinc-800/60 hover:text-zinc-900 dark:hover:text-white border border-zinc-200/60 dark:border-zinc-700/60 transition-colors inline-flex items-center gap-1.5 text-xs font-mono"
                  title="Copy Email"
                >
                  {copiedEmail ? <FaCheck className="text-emerald-500" size={14} /> : <FaCopy size={14} />}
                  <span className="hidden sm:inline">{copiedEmail ? "Copied!" : "Email"}</span>
                </button>
              </div>
            </div>

            {/* Right Column: Visual Avatar + Telemetry Thought Bubble + Floating Badges */}
            <div className="lg:col-span-5 relative flex justify-center items-center py-4">
              
              {/* Telemetry Thought Bubble (Ubuntu Dock / Genie Minimize-and-Expand Animation) */}
              <div className="absolute -top-12 sm:-top-14 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={telemetryIndex}
                    initial={{ opacity: 0, scale: 0.05, y: 60, transformOrigin: "bottom center" }}
                    animate={{ opacity: 1, scale: 1, y: 0, transformOrigin: "bottom center" }}
                    exit={{ opacity: 0, scale: 0.05, y: 60, transformOrigin: "bottom center" }}
                    transition={{
                      duration: 0.42,
                      ease: [0.34, 1.56, 0.64, 1]
                    }}
                    className="relative px-3.5 py-1.5 rounded-2xl bg-zinc-900/95 dark:bg-black/95 border border-emerald-500/50 text-emerald-400 font-mono text-[11px] sm:text-xs shadow-2xl backdrop-blur-xl flex items-center gap-2 whitespace-nowrap"
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    <span>{telemetryValue || "sys::kernel::init"}</span>

                    {/* Speech balloon tail pointing down to robot head */}
                    <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-zinc-900/95 dark:bg-black/95 border-r border-b border-emerald-500/50 rotate-45" />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Main Avatar Container */}
              <motion.div 
                className="relative z-10 p-3 rounded-full bg-white/40 dark:bg-zinc-900/40 backdrop-blur-xl border border-white/60 dark:border-zinc-700/60 shadow-2xl"
                animate={{ y: ["-6px", "6px"] }}
                transition={{ duration: 3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
              >
                <img 
                  src="/images/robot1.png" 
                  alt="Roushan Robot Avatar" 
                  className="w-48 h-48 sm:w-60 sm:h-60 object-cover rounded-full shadow-inner"
                />
              </motion.div>

              {/* Floating Badge 1: Low-Latency */}
              <motion.div 
                className="absolute top-2 -left-2 sm:left-4 z-20 px-3.5 py-2 rounded-xl bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border border-zinc-200/80 dark:border-zinc-700/80 shadow-lg flex items-center gap-2.5 text-xs font-medium text-zinc-800 dark:text-zinc-200"
                animate={{ y: ["-4px", "4px"] }}
                transition={{ duration: 2.5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut", delay: 0.3 }}
              >
                <div className="w-6 h-6 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">⚡</div>
                <span>Low-Latency Core</span>
              </motion.div>

              {/* Floating Badge 2: Neural ML */}
              <motion.div 
                className="absolute bottom-4 -right-2 sm:right-4 z-20 px-3.5 py-2 rounded-xl bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border border-zinc-200/80 dark:border-zinc-700/80 shadow-lg flex items-center gap-2.5 text-xs font-medium text-zinc-800 dark:text-zinc-200"
                animate={{ y: ["4px", "-4px"] }}
                transition={{ duration: 2.8, repeat: Infinity, repeatType: "reverse", ease: "easeInOut", delay: 0.6 }}
              >
                <div className="w-6 h-6 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold">🧠</div>
                <span>Neural & ML Models</span>
              </motion.div>

              {/* Floating Badge 3: Distributed Consensus */}
              <motion.div 
                className="absolute -bottom-2 left-6 sm:left-12 z-20 px-3.5 py-2 rounded-xl bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border border-zinc-200/80 dark:border-zinc-700/80 shadow-lg flex items-center gap-2.5 text-xs font-medium text-zinc-800 dark:text-zinc-200"
                animate={{ y: ["-3px", "3px"] }}
                transition={{ duration: 3.2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut", delay: 0.9 }}
              >
                <div className="w-6 h-6 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">🌐</div>
                <span>Distributed Systems</span>
              </motion.div>
            </div>

          </div>

          {/* Clickable Down Arrow to transition to Scene 2 */}
          <button 
            onClick={() => setCurrentSlide(1)}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-zinc-400 hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-200 transition-colors cursor-pointer group select-none"
            aria-label="Scroll to Engineering Architecture"
          >
            <span className="text-[10px] tracking-widest uppercase font-mono group-hover:underline">Explore Architecture</span>
            <motion.div 
              animate={{ y: [0, 4, 0] }} 
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
              className="p-1 rounded-full bg-white/60 dark:bg-zinc-800/60 border border-zinc-200/80 dark:border-zinc-700/80"
            >
              <FaChevronDown size={12} />
            </motion.div>
          </button>
        </section>

        {/* ========================================================= */}
        {/* SCENE 2: THE 3 CORE ENGINEERING PILLARS */}
        {/* ========================================================= */}
        <section className="h-screen w-full flex flex-col justify-between items-center relative px-6 pt-20 pb-6 z-10">
          
          {/* Clickable Up Arrow to return to Hero */}
          <button 
            onClick={() => setCurrentSlide(0)}
            className="flex items-center gap-1.5 text-xs font-mono text-zinc-400 hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-200 transition-colors cursor-pointer select-none"
            aria-label="Return to Intro"
          >
            <FaChevronUp size={11} />
            <span>Back to Intro</span>
          </button>

          <div className="max-w-6xl w-full space-y-6 my-auto">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <span className="text-xs font-mono uppercase tracking-widest text-primary dark:text-dark-primary font-bold">
                Engineering Architecture
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
                Core Engineering Focus
              </h2>
              <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Architecting high-scale infrastructure from low-level CPU concurrency algorithms to modern machine learning applications.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
              {pillars.map((pillar, idx) => (
                <motion.div
                  key={pillar.number}
                  initial={{ opacity: 0, y: 30 }}
                  animate={currentSlide === 1 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.5, delay: idx * 0.12, ease: "easeOut" }}
                  className="group relative rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md p-6 flex flex-col justify-between hover:border-zinc-400 dark:hover:border-zinc-600 transition-all duration-300 shadow-sm hover:shadow-xl"
                >
                  <div className="space-y-4">
                    {/* Top Bar: Icon and Number */}
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200/60 dark:border-zinc-700/60 group-hover:scale-110 transition-transform duration-200">
                        {pillar.icon}
                      </div>
                      <span className="text-2xl font-mono font-bold text-zinc-300 dark:text-zinc-700 group-hover:text-zinc-400 dark:group-hover:text-zinc-500 transition-colors">
                        {pillar.number}
                      </span>
                    </div>

                    {/* Title & Description */}
                    <div className="space-y-2">
                      <h3 className="text-lg font-bold text-zinc-900 dark:text-white leading-snug">
                        {pillar.title}
                      </h3>
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </div>

                  {/* Tech Tags */}
                  <div className="pt-4 mt-4 border-t border-zinc-200/60 dark:border-zinc-800/60">
                    <div className="flex flex-wrap gap-1.5">
                      {pillar.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-[11px] px-2 py-0.5 rounded-md font-mono bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200/40 dark:border-zinc-700/40"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Integrated Minimal Footer in Scene 2 */}
          <div className="w-full max-w-6xl text-center text-xs font-medium text-zinc-600 dark:text-zinc-300 pt-3 border-t border-zinc-200/80 dark:border-zinc-700/60 select-none">
            <span>&copy; {new Date().getFullYear()} Roushan. All Rights Reserved.</span>
          </div>
        </section>

      </motion.div>
    </div>
  );
};

export default Home;
